/**
 * Auswahlfeld mit Texteingabe: tippen filtert, statt zu scrollen.
 *
 * Fuer Listen mit vielen Eintraegen (Kunden, Lieferanten, Warengruppen)
 * ist eine Klappliste unbrauchbar - hier wird stattdessen getippt wie im
 * Artikelwaehler. Kurze Aufzaehlungen (Zahlungsweg, Steuersatz) bleiben
 * bewusst Klapplisten: Dort ist Aufklappen schneller als Tippen.
 *
 * Zwei Kniffe:
 *
 *   - Die Vorschlagsliste haengt per Portal am Dokument, damit scrollbare
 *     Rahmen und Dialoge sie nicht abschneiden (wie im ItemPicker).
 *   - Waehrend des Tippens vervollstaendigt das Feld den ersten passenden
 *     Wortanfang ("Bau" -> "Baumann", Rest markiert). Enter oder Tab
 *     uebernimmt den hervorgehobenen Treffer; beim Loeschen wird bewusst
 *     nicht wieder aufgefuellt, sonst kaempft man gegen das Feld an.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

export type SearchSelectOption = {
  value: string;
  label: string;
};

type Anchor = { top: number; bottom: number; left: number; width: number };

/** Alle Suchwoerter muessen in der Beschriftung vorkommen. */
function filterOptions(options: SearchSelectOption[], search: string): SearchSelectOption[] {
  const terms = search.trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (terms.length === 0) return options.slice(0, 50);
  return options
    .filter((option) => {
      const haystack = option.label.toLowerCase();
      return terms.every((term) => haystack.includes(term));
    })
    .slice(0, 50);
}

/**
 * Liefert das Wort der Beschriftung, das mit der Eingabe beginnt - fuer
 * die Vervollstaendigung. "bau" trifft in "K-1002 · Baumann Haustechnik"
 * das Wort "Baumann".
 */
function completingWord(label: string, typed: string): string | null {
  const lower = label.toLowerCase();
  const needle = typed.toLowerCase();

  let from = 0;
  while (from <= lower.length - needle.length) {
    const at = lower.indexOf(needle, from);
    if (at === -1) return null;

    const isWordStart = at === 0 || /[\s·,.()/-]/.test(label[at - 1]!);
    if (isWordStart) {
      const rest = label.slice(at);
      const word = /^[^\s·,]+/.exec(rest)?.[0] ?? rest;
      return word.length > typed.length ? word : null;
    }
    from = at + 1;
  }
  return null;
}

export function SearchSelect({
  value,
  onChange,
  options,
  placeholder = 'Tippen zum Suchen …',
  emptyLabel,
  disabled,
  invalid,
}: {
  /** Aktueller Wert; leere Zeichenkette heisst "nichts gewaehlt". */
  value: string;
  onChange: (value: string) => void;
  options: SearchSelectOption[];
  placeholder?: string;
  /**
   * Beschriftung des Eintrags "nichts waehlen". Ohne Angabe gibt es keinen
   * solchen Eintrag - das Feld ist dann eine Pflichtauswahl.
   */
  emptyLabel?: string;
  disabled?: boolean;
  invalid?: boolean;
}) {
  /** Was tatsaechlich getippt wurde - danach wird gefiltert. */
  const [search, setSearch] = useState('');
  /** Was im Feld steht: Eingabe plus markierte Vervollstaendigung. */
  const [display, setDisplay] = useState('');
  const [open, setOpen] = useState(false);
  const [highlighted, setHighlighted] = useState(0);
  const [anchor, setAnchor] = useState<Anchor | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  /** Nach Loeschen-Tasten wird einmal nicht vervollstaendigt. */
  const suppressCompletion = useRef(false);

  const selectedLabel = options.find((option) => option.value === value)?.label ?? '';

  const results = useMemo(() => {
    const matching = filterOptions(options, search);
    const rows: (SearchSelectOption & { isEmpty?: boolean })[] = [...matching];
    // Der Leereintrag steht oben, aber nur solange nicht getippt wird -
    // wer tippt, sucht einen echten Eintrag.
    if (emptyLabel && search.trim() === '') rows.unshift({ value: '', label: emptyLabel, isEmpty: true });
    return rows;
  }, [options, search, emptyLabel]);

  // Die Vervollstaendigung als Markierung setzen, sobald das Feld den
  // ergaenzten Text anzeigt.
  useEffect(() => {
    if (display.length > search.length) {
      inputRef.current?.setSelectionRange(search.length, display.length);
    }
  }, [display, search]);

  function updateAnchor() {
    const rect = containerRef.current?.getBoundingClientRect();
    if (rect) setAnchor({ top: rect.top, bottom: rect.bottom, left: rect.left, width: rect.width });
  }

  useEffect(() => {
    if (!open) return;

    function handleClick(event: MouseEvent) {
      const target = event.target as Node;
      if (containerRef.current?.contains(target)) return;
      if (listRef.current?.contains(target)) return;
      setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    window.addEventListener('scroll', updateAnchor, true);
    window.addEventListener('resize', updateAnchor);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      window.removeEventListener('scroll', updateAnchor, true);
      window.removeEventListener('resize', updateAnchor);
    };
  }, [open]);

  function choose(option: SearchSelectOption) {
    onChange(option.value);
    setSearch('');
    setDisplay('');
    setOpen(false);
  }

  function handleTyped(typed: string) {
    setSearch(typed);
    setDisplay(typed);
    setOpen(true);
    updateAnchor();

    if (suppressCompletion.current || typed.trim() === '') {
      suppressCompletion.current = false;
      setHighlighted(0);
      return;
    }

    // Ersten Treffer suchen, dessen Beschriftung ein Wort enthaelt, das
    // mit der Eingabe beginnt - der wird vervollstaendigt und in der
    // Liste hervorgehoben.
    const matching = filterOptions(options, typed);
    for (let index = 0; index < matching.length; index++) {
      const word = completingWord(matching[index]!.label, typed);
      if (word) {
        setDisplay(typed + word.slice(typed.length));
        setHighlighted(index);
        return;
      }
    }
    setHighlighted(0);
  }

  const maxHeight = 280;
  const openUp =
    anchor !== null &&
    anchor.bottom + maxHeight + 4 > window.innerHeight &&
    anchor.top > maxHeight + 4;

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <input
        // "off" ignoriert Chrome bei Feldern, die es für Adressfelder hält, und
        // bietet gespeicherte Adressen an. Ein anderes Token unterdrückt das.
        autoComplete="one-time-code"
        ref={inputRef}
        className={`input${invalid ? ' invalid' : ''}`}
        value={open ? display : selectedLabel}
        disabled={disabled}
        placeholder={open || !selectedLabel ? placeholder : selectedLabel}
        onFocus={() => {
          setSearch('');
          setDisplay('');
          setHighlighted(0);
          setOpen(true);
          updateAnchor();
        }}
        onChange={(event) => handleTyped(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Backspace' || event.key === 'Delete') {
            suppressCompletion.current = true;
            return;
          }
          if (!open || results.length === 0) {
            if (event.key === 'Escape') setOpen(false);
            return;
          }

          if (event.key === 'ArrowDown') {
            event.preventDefault();
            setHighlighted((current) => Math.min(current + 1, results.length - 1));
          } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            setHighlighted((current) => Math.max(current - 1, 0));
          } else if (event.key === 'Enter') {
            event.preventDefault();
            const option = results[highlighted];
            if (option) choose(option);
          } else if (event.key === 'Tab') {
            // Tab uebernimmt den Vorschlag und laesst den Fokus weiterziehen.
            const option = results[highlighted];
            if (option && search.trim() !== '') choose(option);
            else setOpen(false);
          } else if (event.key === 'Escape') {
            setOpen(false);
          }
        }}
      />

      {open && anchor
        ? createPortal(
            <div
              ref={listRef}
              style={{
                position: 'fixed',
                // Ueber dem Dialog-Hintergrund (z-index 50), sonst verdeckt ein
                // Dialog die Aufklappliste seiner eigenen Suchfelder.
                zIndex: 60,
                ...(openUp
                  ? { bottom: window.innerHeight - anchor.top + 2 }
                  : { top: anchor.bottom + 2 }),
                left: anchor.left,
                width: Math.max(anchor.width, 300),
                background: 'var(--bg-raised)',
                border: '1px solid var(--border-strong)',
                borderRadius: 'var(--radius-sm)',
                boxShadow: 'var(--shadow-lg)',
                maxHeight,
                overflowY: 'auto',
              }}
            >
              {results.length === 0 ? (
                <div className="faint" style={{ padding: '8px 10px', fontSize: 13 }}>
                  Kein Eintrag passt zu „{search}".
                </div>
              ) : (
                results.map((option, index) => (
                  <button
                    key={option.value || '∅'}
                    type="button"
                    onMouseDown={(event) => {
                      event.preventDefault();
                      choose(option);
                    }}
                    onMouseEnter={() => setHighlighted(index)}
                    style={{
                      display: 'block',
                      width: '100%',
                      padding: '6px 10px',
                      border: 0,
                      textAlign: 'left',
                      cursor: 'pointer',
                      background: index === highlighted ? 'var(--bg-hover)' : 'transparent',
                      color: option.isEmpty ? 'var(--text-faint, inherit)' : 'inherit',
                      font: 'inherit',
                      fontStyle: option.isEmpty ? 'italic' : 'normal',
                    }}
                  >
                    {option.label}
                    {option.value === value && !option.isEmpty ? ' ✓' : ''}
                  </button>
                ))
              )}
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}
